import { format, parseISO, addHours } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Converts a date to Argentina timezone (UTC-3)
 * @param date Date to convert
 * @returns Date adjusted to Argentina timezone
 */
export const toArgentinaDate = (date: Date | string): Date => {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  
  // Create a date string in Argentina timezone
  const argentinaDateString = inputDate.toLocaleString('en-US', {
    timeZone: 'America/Argentina/Buenos_Aires'
  });
  
  // Parse the date string back to a Date object
  return new Date(argentinaDateString);
};

/**
 * Gets the current date in Argentina timezone (UTC-3)
 * @returns Current date in Argentina timezone
 */
export const getCurrentArgentinaDate = (): Date => {
  return toArgentinaDate(new Date());
};

/**
 * Formats a date to Argentina timezone (UTC-3) with the specified format
 * @param date Date to format
 * @param formatStr Format string (date-fns format)
 * @returns Formatted date string in Argentina timezone
 */
export const formatArgentinaDate = (
  date: Date | string | null | undefined,
  formatStr: string = 'dd/MM/yyyy'
): string => {
  if (!date) return '';
  
  const argentinaDate = toArgentinaDate(typeof date === 'string' ? parseISO(date) : date);
  return format(argentinaDate, formatStr, { locale: es });
};

/**
 * Converts a date to ISO string in Argentina timezone (UTC-3)
 * @param date Date to convert
 * @returns ISO string in Argentina timezone (YYYY-MM-DD)
 */
export const toArgentinaISOString = (date: Date | string): string => {
  const argentinaDate = toArgentinaDate(date);
  return argentinaDate.toISOString().split('T')[0];
};

/**
 * Gets the current date in Argentina timezone (UTC-3) as an ISO string
 * @returns Current date in Argentina timezone as an ISO string (YYYY-MM-DD)
 */
export const getCurrentArgentinaISOString = (): string => {
  return toArgentinaISOString(new Date());
};

/**
 * Adjusts a date input for form fields to Argentina timezone (UTC-3)
 * @param date Date to adjust
 * @returns Date string in YYYY-MM-DD format for form inputs
 */
export const adjustDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const argentinaDate = toArgentinaDate(typeof date === 'string' ? parseISO(date) : date);
  return format(argentinaDate, 'yyyy-MM-dd');
};

/**
 * Converts a date to a database-friendly ISO string with timezone adjustment
 * @param date Date to convert
 * @returns ISO string with timezone adjustment for database storage
 */
export const toDBFriendlyDate = (date: Date | string): string => {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  
  // Add 3 hours to compensate for UTC-3 timezone when storing in database
  // This ensures the date is stored correctly in UTC but represents Argentina's date
  const adjustedDate = addHours(inputDate, 3);
  return adjustedDate.toISOString().split('T')[0];
};